"use client"

import React from "react"
import { cn } from "@/lib/utils"
import { motion } from "framer-motion"
import { Rocket, Globe, Shield, Target, Database, LucideIcon } from "lucide-react"

interface FusionCardContent {
  title: string
  description: string
}

interface CardConfig {
  icon: LucideIcon
  color: string
  borderColor: string
  hoverBorderColor: string
  iconBg: string
  glowColor: string
}

const cardContents: FusionCardContent[] = [
  {
    title: "Campaign engine",
    description:
      "Apply brand rules and playbooks through AI Lab while Creation Desk delivers concepts and assets on deadline. Launch campaigns faster without sacrificing consistency.",
  },
  {
    title: "Localisation at scale",
    description:
      "Turn one master campaign into market-ready creative across countries, segments, and languages. Preserving tone, protecting claims, maintaining brand integrity.",
  },
  {
    title: "Regulated categories",
    description:
      "Embed legal, medical, or financial guardrails into your workspace. Teams move faster because compliance is built into the workflow, not a bottleneck.",
  },
  {
    title: "Sales enablement & GTM",
    description:
      "Feed decks, battlecards, and FAQs into gimmefy. Generate on-brand one-pagers and follow-ups for every sales scenario, aligned with your GTM strategy.",
  },
  {
    title: "Knowledge-heavy organisations",
    description:
      "Structure your knowledge base with AI Lab so every piece of content references your proprietary data and expertise. Turn institutional knowledge into competitive advantage.",
  },
]

const cardConfigs: CardConfig[] = [
  {
    icon: Rocket,
    color: "blue",
    borderColor: "border-l-blue-500",
    hoverBorderColor: "hover:border-blue-500/50",
    iconBg: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
    glowColor: "hover:shadow-blue-500/20",
  },
  {
    icon: Globe,
    color: "green",
    borderColor: "border-l-green-500",
    hoverBorderColor: "hover:border-green-500/50",
    iconBg: "bg-green-500/10 text-green-600 dark:text-green-400",
    glowColor: "hover:shadow-green-500/20",
  },
  {
    icon: Shield,
    color: "purple",
    borderColor: "border-l-purple-500",
    hoverBorderColor: "hover:border-purple-500/50",
    iconBg: "bg-purple-500/10 text-purple-600 dark:text-purple-400",
    glowColor: "hover:shadow-purple-500/20",
  },
  {
    icon: Target,
    color: "orange",
    borderColor: "border-l-orange-500",
    hoverBorderColor: "hover:border-orange-500/50",
    iconBg: "bg-orange-500/10 text-orange-600 dark:text-orange-400",
    glowColor: "hover:shadow-orange-500/20",
  },
  {
    icon: Database,
    color: "cyan",
    borderColor: "border-l-cyan-500",
    hoverBorderColor: "hover:border-cyan-500/50",
    iconBg: "bg-cyan-500/10 text-cyan-600 dark:text-cyan-400",
    glowColor: "hover:shadow-cyan-500/20",
  },
]

const PlusCard: React.FC<{
  className?: string
  title: string
  description: string
  config: CardConfig
  index: number
}> = ({ className = "", title, description, config, index }) => {
  const Icon = config.icon
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: 0.1 * index }}
      className={cn(
        "relative border border-border/50 rounded-xl p-6 min-h-[200px]",
        "flex flex-col justify-between transition-all duration-300",
        "bg-card/60 backdrop-blur-sm",
        "border-l-4",
        config.borderColor,
        config.hoverBorderColor,
        config.glowColor,
        "hover:shadow-xl hover:-translate-y-1",
        className
      )}
    >
      <CornerPlusIcons color={config.color} />
      
      {/* Icon */}
      <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center mb-4", config.iconBg)}>
        <Icon className="w-5 h-5" />
      </div>
      
      {/* Content */}
      <div className="relative z-10 space-y-3">
        <h3 className="text-xl font-bold text-foreground">
          {title}
        </h3>
        <p className="text-muted-foreground leading-relaxed">{description}</p>
      </div>
    </motion.div>
  )
}

const CornerPlusIcons = ({ color }: { color: string }) => {
  const colorClass = `text-${color}-500/40`
  return (
    <>
      <PlusIcon className={cn("absolute -top-3 -left-3", colorClass)} />
      <PlusIcon className={cn("absolute -top-3 -right-3", colorClass)} />
      <PlusIcon className={cn("absolute -bottom-3 -left-3", colorClass)} />
      <PlusIcon className={cn("absolute -bottom-3 -right-3", colorClass)} />
    </>
  )
}

const PlusIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    width={24}
    height={24}
    strokeWidth="1"
    stroke="currentColor"
    className={cn("size-6", className)}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m6-6H6" />
  </svg>
)

interface FusionBentoCardsProps {
  heading?: string
  subheading?: string
  className?: string
}

export default function FusionBentoCards({ 
  heading = "How gimmefy Fusion fits your organisation",
  subheading = "Every enterprise has its own mess: legacy stacks, regional teams, regulatory constraints. Fusion adapts to your workflows instead of forcing a rebuild.",
  className
}: FusionBentoCardsProps) {
  return (
    <section className={cn("py-16 md:py-24 relative overflow-hidden", className)}>
      {/* Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50/40 via-background to-purple-50/40 dark:from-blue-900/10 dark:via-background dark:to-purple-900/10" />
      
      {/* Decorative Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div 
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3]
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-20 left-10 w-72 h-72 bg-blue-400/10 rounded-full blur-3xl" 
        />
        <motion.div 
          animate={{ 
            scale: [1.2, 1, 1.2],
            opacity: [0.3, 0.5, 0.3]
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          className="absolute bottom-20 right-10 w-72 h-72 bg-purple-400/10 rounded-full blur-3xl" 
        />
        <motion.div 
          animate={{ 
            scale: [1, 1.3, 1],
            opacity: [0.2, 0.4, 0.2]
          }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-green-400/5 rounded-full blur-3xl" 
        />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mx-auto text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            {heading}
          </h2>
          <p className="text-lg text-muted-foreground">
            {subheading}
          </p>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 auto-rows-auto gap-6">
          <PlusCard {...cardContents[0]} config={cardConfigs[0]} index={0} className="lg:col-span-3 lg:row-span-2" />
          <PlusCard {...cardContents[1]} config={cardConfigs[1]} index={1} className="lg:col-span-3 lg:row-span-2" />
          <PlusCard {...cardContents[2]} config={cardConfigs[2]} index={2} className="lg:col-span-2 lg:row-span-1" />
          <PlusCard {...cardContents[3]} config={cardConfigs[3]} index={3} className="lg:col-span-2 lg:row-span-1" />
          <PlusCard {...cardContents[4]} config={cardConfigs[4]} index={4} className="lg:col-span-2 lg:row-span-1" />
        </div>
      </div>
    </section>
  )
}
